import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import BugForm from './BugForm';
import BugFilters from './BugFilters';
import api from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const SheetView = () => {
  const { sheetId } = useParams();
  const [sheet, setSheet] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [filteredBugs, setFilteredBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [editBug, setEditBug] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
  });

  useEffect(() => {
    fetchSheet();
    fetchBugs();
    fetchDashboardData();
  }, [sheetId]);

  useEffect(() => {
    fetchBugs();
  }, [filters, pagination.page, pagination.pageSize]);

  const fetchSheet = async () => {
    try {
      const response = await api.get(`/sheets/${sheetId}`);
      setSheet(response.data);
    } catch (error) {
      setError('Failed to fetch sheet details');
    }
  };

  const fetchBugs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        ...filters,
      };
      const response = await api.get(`/bugs/sheet/${sheetId}`, { params });
      setBugs(response.data.bugs);
      setFilteredBugs(response.data.bugs);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      setError('Failed to fetch bugs');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get(`/reports/dashboard/${sheetId}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const handleDelete = async (bugId) => {
    if (window.confirm('Are you sure you want to delete this bug?')) {
      try {
        await api.delete(`/bugs/${bugId}`);
        fetchBugs();
        fetchDashboardData();
      } catch (error) {
        setError('Failed to delete bug');
      }
    }
  };

  const handleFormSubmit = () => {
    setOpenForm(false);
    setEditBug(null);
    fetchBugs();
    fetchDashboardData();
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await api.get(`/bugs/export/${sheetId}/${format}?${params}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sheet.name}-bugs.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('importFile', file);

    try {
      const response = await api.post(`/bugs/import/${sheetId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      alert(response.data.message);
      if (response.data.errors > 0) {
        console.log('Import errors:', response.data.errorDetails);
      }
      
      fetchBugs();
      fetchDashboardData();
    } catch (error) {
      setError('Import failed');
    }
    
    // Reset file input
    event.target.value = '';
  };

  const columns = [
    { field: 'serialNo', headerName: 'S.No', width: 80 },
    { 
      field: 'bugId', 
      headerName: 'Bug ID', 
      width: 80,
      renderCell: (params) => params.row.displayBugId || params.row.serialNo
    },
    { 
      field: 'dateOfCreation', 
      headerName: 'Date Created', 
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    { field: 'errorDetails', headerName: 'Error Details', width: 200, flex: 1 },
    { field: 'bugFoundInVersion', headerName: 'Version', width: 100 },
    { field: 'raisedBy', headerName: 'Raised By', width: 120 },
    { field: 'assignedTo', headerName: 'Assigned To', width: 120 },
    { 
      field: 'bugStatus', 
      headerName: 'Status', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={
            params.value === 'Resolved' ? 'success' :
            params.value === 'In progress' ? 'warning' :
            params.value === 'New and Created' ? 'info' : 'default'
          }
        />
      )
    },
    { 
      field: 'priority', 
      headerName: 'Priority', 
      width: 80,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value >= 4 ? 'error' : params.value >= 3 ? 'warning' : 'default'}
        />
      )
    },
    { field: 'type', headerName: 'Type', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setEditBug(params.row);
              setOpenForm(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row._id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && !bugs.length) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {sheet?.name}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenForm(true)}
          >
            Add Bug
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('xlsx')}
          >
            Export XLSX
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('csv')}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('json')}
          >
            Export JSON
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
          >
            Import
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv,.json"
              onChange={handleImport}
            />
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Bug List" />
          <Tab label="Reports" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            <BugFilters onFiltersChange={setFilters} />
            <Box sx={{ height: 600, width: '100%', mt: 2 }}>
              <DataGrid
                rows={filteredBugs}
                columns={columns}
                pageSize={pagination.pageSize}
                page={pagination.page}
                rowCount={pagination.total}
                paginationMode="server"
                onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                onPageSizeChange={(newPageSize) => setPagination(prev => ({ ...prev, pageSize: newPageSize }))}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={loading}
                disableSelectionOnClick
                getRowId={(row) => row._id}
              />
            </Box>
          </Box>
        )}

        {tabValue === 1 && dashboardData && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* Summary Cards */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Bugs
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData.summary.total}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Open Bugs
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {dashboardData.summary.open}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Closed Bugs
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {dashboardData.summary.closed}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      High Priority
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {dashboardData.summary.highPriority}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Bugs by Type Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 400 }}>
                  <Typography variant="h6" gutterBottom>
                    Bugs by Type
                  </Typography>
                  {dashboardData.bugsByType && dashboardData.bugsByType.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dashboardData.bugsByType}
                          dataKey="count"
                          nameKey="_id"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          fill="#8884d8"
                          label={({ _id, count }) => `${_id}: ${count}`}
                        >
                          {dashboardData.bugsByType.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Bugs by Type Bar Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 400 }}>
                  <Typography variant="h6" gutterBottom>
                    Bugs by Type (Bar Chart)
                  </Typography>
                  {dashboardData.bugsByType && dashboardData.bugsByType.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={dashboardData.bugsByType}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="_id" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Bugs by Version Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 400 }}>
                  <Typography variant="h6" gutterBottom>
                    Bugs by Version
                  </Typography>
                  {dashboardData.bugsByVersion && dashboardData.bugsByVersion.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={dashboardData.bugsByVersion}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="_id" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#00C49F" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Bug Status Distribution */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 400 }}>
                  <Typography variant="h6" gutterBottom>
                    Bugs by Status
                  </Typography>
                  {dashboardData.bugsByStatus && dashboardData.bugsByStatus.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dashboardData.bugsByStatus}
                          dataKey="count"
                          nameKey="_id"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          fill="#FFBB28"
                          label={({ _id, count }) => `${_id}: ${count}`}
                        >
                          {dashboardData.bugsByStatus.map((entry, index) => (
                            <Cell key={`status-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Bug Form Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editBug ? 'Edit Bug' : 'Add New Bug'}
        </DialogTitle>
        <DialogContent>
          <BugForm
            bug={editBug}
            sheetId={sheetId}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenForm(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SheetView;